import { api, ApiError } from '../api/client';
import type { AdminSummary, ApiKeySummary } from '../api/types';
import { initialsFromName } from '../lib/format';
import { escapeHtml } from '../lib/html';
import { relativeTime } from '../lib/time';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function adminStatusChip(admin: AdminSummary): { label: string; className: string } {
  if (!admin.enabled) return { label: 'Disabled', className: 'status-disabled' };
  if (admin.status === 'FORCE_CHANGE_PASSWORD') return { label: 'Pending invite', className: 'status-pending' };
  return { label: 'Active', className: 'status-active' };
}

function keyStatusChip(key: ApiKeySummary): { label: string; className: string } {
  return key.status === 'revoked'
    ? { label: 'Revoked', className: 'status-disabled' }
    : { label: 'Active', className: 'status-active' };
}

export function mount(root: HTMLElement): () => void {
  root.innerHTML = `
    <div class="admin-page">
      <div class="admin-page-inner">
        <div class="admin-page-head">
          <div>
            <h1>Admin</h1>
            <p class="subtitle">Manage who can administer this account and what can access it.</p>
          </div>
        </div>

        <h2>Admins</h2>
        <p class="subtitle">People who can view locations and manage this account.</p>
        <div id="admins-error" class="admin-error" hidden></div>
        <div class="admins-panel">
          <div class="admins-panel-head">
            <form id="invite-admin-form" style="display:contents">
              <input type="email" id="invite-admin-email" class="text-input" placeholder="Email" required />
              <input type="text" id="invite-admin-name" class="text-input" placeholder="Name" required minlength="1" />
              <button type="submit" class="btn-accent">Invite admin</button>
            </form>
          </div>
          <div id="admins-list">Loading&hellip;</div>
        </div>

        <h2>MCP access</h2>
        <p class="subtitle">API keys that let Claude (or other authenticated clients) read family location data over MCP.</p>
        <div id="keys-error" class="admin-error" hidden></div>
        <div id="key-reveal" class="key-reveal" hidden>
          <div class="key-reveal-head">New key issued &mdash; copy it now, you won&rsquo;t see it again</div>
          <div class="key-reveal-row">
            <code id="key-reveal-value"></code>
            <button type="button" id="key-reveal-copy" class="btn-ghost small">Copy</button>
          </div>
          <div class="key-reveal-warning">This is the only time the full key is shown. Store it somewhere safe &mdash; anyone with it can read all family location data.</div>
          <button type="button" id="key-reveal-dismiss" class="btn-text-danger small">Dismiss</button>
        </div>
        <div class="admins-panel">
          <div class="admins-panel-head">
            <form id="issue-key-form" style="display:contents">
              <input type="text" id="issue-key-label" class="text-input" placeholder="Label (e.g. Claude MCP)" required minlength="1" />
              <button type="submit" class="btn-accent">Issue new key</button>
            </form>
          </div>
          <div id="keys-list">Loading&hellip;</div>
        </div>

        <h2>Android app</h2>
        <p class="subtitle">Download the tracker app to install on a family member's phone.</p>
        <div class="admins-panel">
          <div class="app-download-row">
            <div class="app-download-info">
              <div class="app-download-name">Family Tracker for Android</div>
              <div class="app-download-meta" id="app-download-meta">&hellip;</div>
            </div>
            <a class="btn-accent" id="app-download-btn" href="/family-tracker.apk" download>Download APK</a>
          </div>
          <p class="app-download-hint">On the phone, open this page, tap <strong>Download APK</strong>, then open the downloaded file to install. Android may ask you to allow installing apps from your browser the first time.</p>
        </div>
      </div>
    </div>
  `;

  const adminsErrorBox = root.querySelector<HTMLDivElement>('#admins-error')!;
  const adminsList = root.querySelector<HTMLDivElement>('#admins-list')!;
  const inviteAdminForm = root.querySelector<HTMLFormElement>('#invite-admin-form')!;
  const inviteEmailInput = root.querySelector<HTMLInputElement>('#invite-admin-email')!;
  const inviteNameInput = root.querySelector<HTMLInputElement>('#invite-admin-name')!;

  const keysErrorBox = root.querySelector<HTMLDivElement>('#keys-error')!;
  const keysList = root.querySelector<HTMLDivElement>('#keys-list')!;
  const issueKeyForm = root.querySelector<HTMLFormElement>('#issue-key-form')!;
  const issueKeyLabelInput = root.querySelector<HTMLInputElement>('#issue-key-label')!;
  const keyReveal = root.querySelector<HTMLDivElement>('#key-reveal')!;
  const keyRevealValue = root.querySelector<HTMLElement>('#key-reveal-value')!;
  const keyRevealCopy = root.querySelector<HTMLButtonElement>('#key-reveal-copy')!;
  const keyRevealDismiss = root.querySelector<HTMLButtonElement>('#key-reveal-dismiss')!;

  const currentIdentity = api.currentAdminIdentity();

  let disposed = false;

  // Fill the app download card from the published version metadata; if no APK
  // has been published yet, /app-version.json 404s and the button is disabled.
  const appMeta = root.querySelector<HTMLDivElement>('#app-download-meta')!;
  const appBtn = root.querySelector<HTMLAnchorElement>('#app-download-btn')!;
  fetch('/app-version.json', { cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('not published'))))
    .then((v: { versionName: string; versionCode: number }) => {
      if (disposed) return;
      appMeta.textContent = `Version ${v.versionName} (build ${v.versionCode})`;
    })
    .catch(() => {
      if (disposed) return;
      appMeta.textContent = 'Not published yet';
      appBtn.classList.add('disabled');
      appBtn.setAttribute('aria-disabled', 'true');
      appBtn.removeAttribute('href');
    });

  function hideKeyReveal(): void {
    keyReveal.hidden = true;
    keyRevealValue.textContent = '';
  }

  function showKeysError(err: unknown): void {
    const message = err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
    keysErrorBox.textContent = message;
    keysErrorBox.hidden = false;
  }

  function clearKeysError(): void {
    keysErrorBox.hidden = true;
    keysErrorBox.textContent = '';
  }

  function renderKeys(keys: ApiKeySummary[]): void {
    keysList.innerHTML = '';

    if (keys.length === 0) {
      keysList.innerHTML = '<p class="member-no-devices">No API keys yet.</p>';
      return;
    }

    for (const key of keys) {
      const chip = keyStatusChip(key);
      const row = document.createElement('div');
      row.className = 'admin-row';
      row.innerHTML = `
        <div class="admin-row-info">
          <div class="admin-row-name">${escapeHtml(key.label)}</div>
          <div class="admin-row-email">${escapeHtml(key.prefix)}&hellip;</div>
        </div>
        <span class="status-chip ${chip.className}">${chip.label}</span>
        <span class="key-last-used">${key.last_used_at ? `Last used ${relativeTime(key.last_used_at)}` : 'Never used'}</span>
      `;

      if (key.status === 'active') {
        const revokeBtn = document.createElement('button');
        revokeBtn.type = 'button';
        revokeBtn.className = 'btn-text-danger small';
        revokeBtn.textContent = 'Revoke';
        revokeBtn.addEventListener('click', async () => {
          if (!window.confirm(`Revoke the API key "${key.label}"? Anything using it will immediately lose access.`)) {
            return;
          }
          clearKeysError();
          try {
            await api.revokeApiKey(key.key_id);
            await loadKeys();
          } catch (err) {
            showKeysError(err);
          }
        });
        row.appendChild(revokeBtn);
      }

      keysList.appendChild(row);
    }
  }

  async function loadKeys(): Promise<void> {
    try {
      const keys = await api.listApiKeys();
      if (disposed) return;
      renderKeys(keys);
    } catch (err) {
      if (disposed) return;
      showKeysError(err);
      keysList.innerHTML = '';
    }
  }

  function showAdminsError(err: unknown): void {
    const message = err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
    adminsErrorBox.textContent = message;
    adminsErrorBox.hidden = false;
  }

  function clearAdminsError(): void {
    adminsErrorBox.hidden = true;
    adminsErrorBox.textContent = '';
  }

  function renderAdmins(admins: AdminSummary[]): void {
    adminsList.innerHTML = '';

    if (admins.length === 0) {
      adminsList.innerHTML = '<p class="member-no-devices">No admins yet.</p>';
      return;
    }

    for (const admin of admins) {
      const chip = adminStatusChip(admin);
      const row = document.createElement('div');
      row.className = 'admin-row';
      row.innerHTML = `
        <span class="admin-row-avatar">${escapeHtml(initialsFromName(admin.name))}</span>
        <div class="admin-row-info">
          <div class="admin-row-name">${escapeHtml(admin.name)}</div>
          <div class="admin-row-email">${escapeHtml(admin.email)}</div>
        </div>
        <span class="status-chip ${chip.className}">${chip.label}</span>
      `;

      const isSelf =
        (currentIdentity.username !== null && currentIdentity.username === admin.username) ||
        (currentIdentity.email !== null && currentIdentity.email === admin.email);

      if (!isSelf) {
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'btn-text-danger small';
        removeBtn.textContent = 'Remove';
        removeBtn.addEventListener('click', async () => {
          if (
            !window.confirm(`Remove admin "${admin.name}" (${admin.email})? They will lose portal access immediately.`)
          ) {
            return;
          }
          clearAdminsError();
          try {
            await api.removeAdmin(admin.username);
            await loadAdmins();
          } catch (err) {
            showAdminsError(err);
          }
        });
        row.appendChild(removeBtn);
      }

      adminsList.appendChild(row);
    }
  }

  async function loadAdmins(): Promise<void> {
    try {
      const admins = await api.listAdmins();
      if (disposed) return;
      renderAdmins(admins);
    } catch (err) {
      if (disposed) return;
      showAdminsError(err);
      adminsList.innerHTML = '';
    }
  }

  inviteAdminForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = inviteEmailInput.value.trim();
    const name = inviteNameInput.value.trim();
    if (!email || !name) return;
    if (!EMAIL_RE.test(email)) {
      showAdminsError('Enter a valid email address.');
      return;
    }
    clearAdminsError();
    try {
      await api.inviteAdmin(email, name);
      inviteEmailInput.value = '';
      inviteNameInput.value = '';
      await loadAdmins();
    } catch (err) {
      showAdminsError(err);
    }
  });

  issueKeyForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const label = issueKeyLabelInput.value.trim();
    if (!label) return;
    clearKeysError();
    try {
      const created = await api.createApiKey(label);
      issueKeyLabelInput.value = '';
      keyRevealValue.textContent = created.key;
      keyReveal.hidden = false;
      await loadKeys();
    } catch (err) {
      showKeysError(err);
    }
  });

  keyRevealCopy.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(keyRevealValue.textContent ?? '');
      keyRevealCopy.textContent = 'Copied!';
      window.setTimeout(() => {
        keyRevealCopy.textContent = 'Copy';
      }, 1500);
    } catch {
      showKeysError('Could not copy automatically - select and copy the key manually.');
    }
  });

  keyRevealDismiss.addEventListener('click', () => hideKeyReveal());

  loadAdmins();
  loadKeys();

  return () => {
    disposed = true;
  };
}
